// mejora-web F4: promociones (primera clase / bono de prueba).
// Solo una promoción activa y vigente se muestra en la portada (la más reciente).
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Plus, Edit2, Trash2, Loader2, AlertCircle, X } from 'lucide-react';

import { listPromociones, createPromocion, updatePromocion, deletePromocion, listBonosTipos } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import CamposIdiomas from '../../components/admin/CamposIdiomas';

const FORM_VACIO = {
  tipo: 'primera_clase', titulo_es: '', titulo_en: '', titulo_ru: '', titulo_fr: '',
  descripcion_es: '', descripcion_en: '', descripcion_ru: '', descripcion_fr: '',
  precio_euros: '', bono_tipo_id: '', fecha_inicio: '', fecha_fin: '', activa: false,
};

function hoyISO() { return new Date().toISOString().slice(0, 10); }
function estadoPromo(p) {
  if (!p.activa) return 'inactiva';
  const h = hoyISO();
  if (p.fecha_inicio && p.fecha_inicio > h) return 'programada';
  if (p.fecha_fin && p.fecha_fin < h) return 'caducada';
  return 'en_portada';
}

export default function AdminPromociones() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems((await listPromociones()).data || []); }
    catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const alternar = async (p) => {
    try { await updatePromocion(p.id, { activa: !p.activa }); cargar(); } catch (e) { alert(e.message); }
  };
  const borrar = async (p) => {
    if (!window.confirm(t('admin.confirmar_borrar', { nombre: p.titulo_es }))) return;
    try { await deletePromocion(p.id); cargar(); } catch (e) { alert(e.message); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.mw.promos.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.mw.promos.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}
      {!loading && !error && items.length === 0 && <p className="mw-vacio">{t('admin.mw.promos.vacio')}</p>}

      {!loading && !error && items.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.mw.promos.col.titulo')}</th>
              <th>{t('admin.mw.promos.col.tipo')}</th>
              <th>{t('admin.mw.promos.col.precio')}</th>
              <th>{t('admin.mw.promos.col.fechas')}</th>
              <th>{t('admin.mw.promos.col.estado')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const est = estadoPromo(p);
              return (
                <tr key={p.id}>
                  <td>{p.titulo_es}{p.bono_tipo && <div className="mw-sub">→ {p.bono_tipo.nombre_es}</div>}</td>
                  <td>{t(`admin.mw.promos.tipo.${p.tipo}`)}</td>
                  <td>{Number(p.precio_euros).toFixed(2).replace('.', ',')} €</td>
                  <td>{p.fecha_inicio || '…'} → {p.fecha_fin || '…'}</td>
                  <td>
                    <button type="button" className={'estado-pill mw-pill-btn' + (est === 'en_portada' ? ' estado-pill--ok' : '')} onClick={() => alternar(p)}>
                      {t(`admin.mw.promos.estado.${est}`)}
                    </button>
                  </td>
                  <td className="admin-acciones">
                    <button type="button" onClick={() => setEditar({ ...FORM_VACIO, ...p, bono_tipo_id: p.bono_tipo_id || '', fecha_inicio: p.fecha_inicio || '', fecha_fin: p.fecha_fin || '' })} aria-label={t('admin.editar')}>
                      <Edit2 size={16} strokeWidth={1.5} />
                    </button>
                    <button type="button" onClick={() => borrar(p)} aria-label={t('admin.borrar')}>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editar && <PromoModal item={editar} onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />}
    </div>
  );
}

function PromoModal({ item, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [bonos, setBonos] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const editando = !!item.id;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  useEffect(() => { listBonosTipos().then((r) => setBonos(r.data || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setEnviando(true); setError(null);
    try {
      const payload = {
        tipo: form.tipo,
        titulo_es: form.titulo_es, titulo_en: form.titulo_en, titulo_ru: form.titulo_ru, titulo_fr: form.titulo_fr,
        descripcion_es: form.descripcion_es, descripcion_en: form.descripcion_en,
        descripcion_ru: form.descripcion_ru, descripcion_fr: form.descripcion_fr,
        precio_euros: Number(String(form.precio_euros).replace(',', '.')),
        bono_tipo_id: form.bono_tipo_id || null,
        fecha_inicio: form.fecha_inicio || null,
        fecha_fin: form.fecha_fin || null,
        activa: !!form.activa,
      };
      if (editando) await updatePromocion(item.id, payload); else await createPromocion(payload);
      onGuardado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('admin.error_generico'));
    } finally { setEnviando(false); }
  };

  const bonoSel = bonos.find((b) => b.id === form.bono_tipo_id);
  const precioNum = Number(String(form.precio_euros).replace(',', '.'));
  const desajuste = bonoSel && Number.isFinite(precioNum) && Number(bonoSel.precio_euros) !== precioNum;

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.mw.promos.nueva')}</h2>
        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.mw.promos.col.tipo')}</label>
              <select value={form.tipo} onChange={set('tipo')}>
                <option value="primera_clase">{t('admin.mw.promos.tipo.primera_clase')}</option>
                <option value="bono_prueba">{t('admin.mw.promos.tipo.bono_prueba')}</option>
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.mw.promos.col.precio')} (€)</label>
              <input type="number" step="0.01" min="0" value={form.precio_euros} onChange={set('precio_euros')} required />
            </div>
            <CamposIdiomas prefijo="titulo" etiqueta={t('admin.mw.promos.f.titulo')} form={form} setForm={setForm} requeridoEs
              placeholders={{ es: t('admin.mw.promos.f.titulo_ph') }} />
            <div className="modal-campo full">
              <label>{t('admin.mw.promos.f.bono')}</label>
              <select value={form.bono_tipo_id} onChange={set('bono_tipo_id')}>
                <option value="">{t('admin.mw.promos.f.bono_ninguno')}</option>
                {bonos.map((b) => (
                  <option key={b.id} value={b.id}>{b.nombre_es} · {Number(b.precio_euros)} €{b.activo ? '' : ` (${t('admin.mw.inactivo')})`}</option>
                ))}
              </select>
              <small className="mw-ayuda">{t('admin.mw.promos.ayuda_bono')}</small>
              {desajuste && <small className="mw-aviso">{t('admin.mw.promos.aviso_precio', { precio: Number(bonoSel.precio_euros) })}</small>}
              {bonoSel && !bonoSel.activo && <small className="mw-aviso">{t('admin.mw.promos.aviso_bono_inactivo')}</small>}
            </div>
            <div className="modal-campo">
              <label>{t('admin.mw.promos.f.desde')}</label>
              <input type="date" value={form.fecha_inicio} onChange={set('fecha_inicio')} />
            </div>
            <div className="modal-campo">
              <label>{t('admin.mw.promos.f.hasta')}</label>
              <input type="date" value={form.fecha_fin} onChange={set('fecha_fin')} />
            </div>
            <CamposIdiomas prefijo="descripcion" etiqueta={t('admin.mw.promos.f.descripcion')} form={form} setForm={setForm} multilinea />
            <div className="modal-campo full">
              <label className="mw-check"><input type="checkbox" checked={!!form.activa} onChange={set('activa')} /> {t('admin.mw.promos.f.activa')}</label>
            </div>
          </div>
          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
